import type { LicenseDto } from '@sellokart/shared';
import { Badge } from '@/components/Badge';
import { formatRelative } from '@/lib/format';

/**
 * The things worth acting on, in one component so both views agree. A flag that
 * means one thing on the tenant page and another on the fleet page is worse
 * than no flag at all.
 *
 * All of these are advisory — none blocks a till — and the wording says so.
 * They start a conversation; they do not report an outage.
 */
export function LicenseFlags({ license }: { license: LicenseDto }) {
  const flags: Array<{ label: string; title: string }> = [];

  if (license.cloneDetectedAt) {
    flags.push({
      label: 'Possible clone',
      title:
        `A stale nonce arrived ${formatRelative(license.cloneDetectedAt)} — two copies of this ` +
        'till exist. A restored backup or a migrated VM causes this too, so confirm before revoking. ' +
        'Both copies keep working until you act.',
    });
  }

  if (license.clockAnomalyAt) {
    flags.push({
      label: 'Clock anomaly',
      title:
        `The till reported its clock had gone backwards (${formatRelative(license.clockAnomalyAt)}). ` +
        'Small drift is ignored, so this means a large jump — either a dead CMOS battery or an ' +
        'attempt to extend the licence.',
    });
  }

  if (license.bindingWeak) {
    flags.push({
      label: 'Weak binding',
      title:
        'This till could not bind its anchor to the platform keystore, so copy protection is ' +
        'weaker than normal. Activation is never refused for this — a platform quirk must not ' +
        'stop a shop trading.',
    });
  }

  if (flags.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {flags.map((flag) => (
        <span key={flag.label} title={flag.title}>
          <Badge tone="warning">{flag.label}</Badge>
        </span>
      ))}
    </div>
  );
}
